import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/providers/ApiContextProvider/ApiContextProvider';
import { MONITORING_KEY } from '@/api/constants/queryKeys';
import { MONITORING_LONG_POLL_OPTIONS } from '@/api/constants/queryOptions';
import { handleCatch, handleCatchWithCustomError } from '@/api/helpers';
import {
  IGetMonitoringRequest,
  IMonitoredEntity,
  IGetMonitoringTransfersResponse,
  IMonitoringTreasury,
  IGetMonitoringChainDataResponse,
  IGetMonitoringAlertHistoryResponse,
  IGetPaginatedMonitoringRequest,
  IGetPaginatedMonitoringResponse,
} from '@/api/monitoring/monitoring.types';
import { CustomApiError } from '@/types/error';

export const useGetMonitoring = <TData = IMonitoredEntity[]>(
  { entityType, allExtreme, isBetaData }: IGetMonitoringRequest,
  select?: (data: IMonitoredEntity[]) => TData,
) => {
  const { monitoringApi } = useApi();

  const {
    data: fetchedMonitoredEntities,
    isLoading,
    isSuccess,
    error,
  } = useQuery<IMonitoredEntity[], Error, TData>({
    queryKey: [MONITORING_KEY, 'GET', entityType, allExtreme, isBetaData],
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes
    queryFn: () => {
      return monitoringApi.getMonitoring({ entityType, allExtreme, isBetaData }).catch(reason => {
        return handleCatch(reason, `load monitoring data`);
      });
    },
    select,
  });

  return {
    fetchedMonitoredEntities,
    isLoading,
    isSuccess,
    error,
  };
};

export const useGetPaginatedMonitoring = <TData = IGetPaginatedMonitoringResponse>(
  {
    entityTypes,
    allExtreme,
    displayName,
    sortBy,
    sortOrder,
    isBetaData,
    page,
    size,
  }: IGetPaginatedMonitoringRequest,
  select?: (data: IGetPaginatedMonitoringResponse) => TData,
) => {
  const { monitoringApi } = useApi();

  const {
    data: fetchedPaginatedMonitoredEntities,
    isLoading,
    isFetching,
    isPlaceholderData,
    isSuccess,
    error,
  } = useQuery<IGetPaginatedMonitoringResponse, Error, TData>({
    queryKey: [
      MONITORING_KEY,
      'GET_PAGINATED_MONITORING',
      entityTypes,
      allExtreme,
      displayName,
      sortBy,
      sortOrder,
      isBetaData,
      page,
      size,
    ],
    refetchInterval: 10 * 60 * 1000, // 10 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    staleTime: 10 * 60 * 1000, // 10 minutes
    queryFn: () => {
      return monitoringApi
        .getPaginatedMonitoring({
          entityTypes,
          allExtreme,
          displayName,
          sortBy,
          sortOrder,
          isBetaData,
          page,
          size,
        })
        .catch(reason => {
          return handleCatch(reason, `load paginated monitoring data`);
        });
    },
    placeholderData: keepPreviousData,
    select,
  });

  return {
    fetchedPaginatedMonitoredEntities,
    isLoading,
    isFetching,
    isPlaceholderData,
    isSuccess,
    error,
  };
};

export const useGetMonitoringTransfers = (entityId: string, shouldBeWithdrawalEnabled: boolean) => {
  const { monitoringApi } = useApi();

  const {
    data: fetchedMonitoredTransfers,
    isLoading,
    isSuccess,
    error,
  } = useQuery<IGetMonitoringTransfersResponse, Error | CustomApiError>({
    queryKey: [MONITORING_KEY, 'GET_MONITORED_TRANSFERS', entityId],
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchInterval: 30 * 60 * 1000, // 30 minutes
    queryFn: () => {
      return monitoringApi.getMonitoringTransfers(entityId).catch(reason => {
        return handleCatchWithCustomError(reason, `load monitored transfers data`);
      });
    },
    enabled: !!entityId && shouldBeWithdrawalEnabled,
    retry: (failureCount, error) => {
      const status = (error as CustomApiError).status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 3;
    },
    refetchOnWindowFocus: false,
  });

  return {
    fetchedMonitoredTransfers,
    isLoading,
    isSuccess,
    error,
  };
};

export const useGetMonitoringTreasury = (entityId: string) => {
  const { monitoringApi } = useApi();

  const {
    data: fetchedMonitoringTreasury,
    isLoading,
    isSuccess,
    error,
  } = useQuery<IMonitoringTreasury[], Error>({
    queryKey: [MONITORING_KEY, 'GET_MONITORING_TREASURY', entityId],
    ...MONITORING_LONG_POLL_OPTIONS,
    queryFn: () => {
      return monitoringApi.getMonitoringTreasury(entityId).catch(reason => {
        return handleCatch(reason, `load monitoring treasury data`);
      });
    },
    enabled: !!entityId,
  });

  return {
    fetchedMonitoringTreasury,
    isLoading,
    isSuccess,
    error,
  };
};

export const useGetMonitoringChainData = (agioId: string, isBetaUserFeature?: boolean) => {
  const { monitoringApi } = useApi();

  const {
    data: fetchedOnChainData,
    isLoading,
    isSuccess,
    error,
  } = useQuery<IGetMonitoringChainDataResponse, Error>({
    queryKey: [MONITORING_KEY, 'GET_MONITORING_CHAIN_DATA', agioId, isBetaUserFeature],
    ...MONITORING_LONG_POLL_OPTIONS,
    queryFn: () => {
      return monitoringApi.getMonitoringChainData(agioId, isBetaUserFeature).catch(reason => {
        return handleCatch(reason, `load monitoring on-chain data`);
      });
    },
    enabled: !!agioId && isBetaUserFeature !== false,
  });

  return {
    fetchedOnChainData,
    isLoading,
    isSuccess,
    error,
  };
};

export const useGetMonitoringAlertHistory = (
  agioId: string,
  isMonitored: boolean,
  isBetaUserFeature?: boolean,
) => {
  const { monitoringApi } = useApi();

  const {
    data: fetchedAlertHistoryData,
    isLoading,
    isSuccess,
    error,
  } = useQuery<IGetMonitoringAlertHistoryResponse, Error>({
    queryKey: [MONITORING_KEY, 'GET_MONITORING_ALERT_HISTORY', agioId, isBetaUserFeature],
    ...MONITORING_LONG_POLL_OPTIONS,
    queryFn: () => {
      return monitoringApi.getMonitoringAlertHistory(agioId, isBetaUserFeature).catch(reason => {
        return handleCatch(reason, `load monitoring alert history data`);
      });
    },
    enabled: !!agioId && !!isMonitored && isBetaUserFeature !== false,
  });

  return {
    fetchedAlertHistoryData,
    isLoading,
    isSuccess,
    error,
  };
};
